import * as React from "react";
import { useNavigate } from "react-router-dom";
import type { ComponentType } from "react";
import {
  Check,
  ChevronDown,
  Copy,
  Edit3,
  ExternalLink,
  Link2,
  Megaphone,
  MessageSquare,
  Pause,
  Play,
  Plus,
  Rss,
  Trash2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Feed } from "@proptr/api-client";
import { EmptyState } from "../components/EmptyState.js";
import {
  HeaderFilter,
  HeaderLabel,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from "../components/table/index.js";
import { AppShell } from "../components/layout/AppShell.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu.js";
import {
  CreatePostOrCampaignModal,
  type CreateKind,
} from "../components/CreatePostOrCampaignModal.js";
import { ConfirmModal } from "../components/ConfirmModal.js";
import { PublishToMetaModal } from "../components/PublishToMetaModal.js";
import { SettingsCard } from "../components/SettingsCard.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { Tabs } from "../components/ui/tabs.js";
import {
  commerceManagerUrl,
  copyText,
  resolveFeedUrl,
} from "../lib/feedUrl.js";
import { cn } from "../lib/utils.js";
import { client } from "../main.js";

// Column shape follows the legacy campaigns list (enterprise-app's
// listCustomCampaigns): the promoted listing, the campaign name, payment
// state, run dates, and delivery stats.
export interface Campaign {
  _id: string;
  /** Post, campaign, or feed — the first column of the list. */
  type: "post" | "campaign" | "feed";
  name: string;
  listingName: string | null;
  listingRef: string | null;
  status: "running" | "pending" | "finished";
  payment: "paid" | "pending" | "failed";
  start: string | null;
  end: string | null;
  impressions: number;
  clicks: number;
  leads: number;
}

export interface CampaignListResponse {
  items: Campaign[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    counts: { all: number; running: number; pending: number; finished: number };
  };
}

// Three views, not one list with a filter. Campaigns, posts and feeds
// share a page because they're all "things you publish", but they don't
// share columns: a feed has no spend, no flight dates and no delivery
// stats, and forcing it through the campaign columns meant a row that
// was mostly em-dashes. One tab per kind lets each own its columns.
type Tab = "campaign" | "post" | "feed";

// Server-owned Meta state. The app id is public (it ships in the SDK
// init); the secret and the stored business token never leave the API.
interface MetaConfig {
  configured: boolean;
  appId: string | null;
  configId: string | null;
  graphVersion: string;
  connection: {
    connectedAt: string | null;
    businesses: Array<{ id: string; name: string }>;
    scopes: string[];
    missingScopes: string[];
  } | null;
}

// Feed status filter, in the first column header where every other list
// page puts its filter.
type FeedStatusFilter = "all" | "live" | "disabled";

const FEED_STATUS_OPTIONS: { value: FeedStatusFilter; label: string }[] = [
  { value: "all", label: "All feeds" },
  { value: "live", label: "Live" },
  { value: "disabled", label: "Disabled" },
];

/** The surface a feed publishes to — the Channel column. */
const CHANNEL_LABEL: Record<string, string> = {
  meta: "Meta",
  whatsapp: "WhatsApp",
};

/** The catalog vertical behind that channel. */
const FORMAT_LABEL: Record<string, string> = {
  meta: "Meta catalog (CSV)",
  whatsapp: "WhatsApp catalog (CSV)",
};

// Meta's brand mark from the shared social-logo set (also used by the
// Link-in-bio column). An <img> rather than a lucide glyph so it keeps
// the official blue instead of inheriting the button's text colour —
// which means it must carry its own size, since the button's icon
// sizing rules only target `svg`.
function MetaLogo({ className }: { className?: string }) {
  return (
    <img
      src="/social/meta.png"
      alt=""
      className={cn("h-4 w-4 shrink-0 object-contain", className)}
    />
  );
}

export function MarketingPage() {
  const navigate = useNavigate();
  // Feeds first: it's the only one of the three with anything in it
  // until the campaigns endpoint exists, so defaulting elsewhere would
  // open the page on a guaranteed empty state.
  const [tab, setTab] = React.useState<Tab>("feed");
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);
  const [data, setData] = React.useState<CampaignListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [feeds, setFeeds] = React.useState<Feed[] | null>(null);
  const [feedStatus, setFeedStatus] = React.useState<FeedStatusFilter>("all");
  const [metaConfig, setMetaConfig] = React.useState<MetaConfig | null>(null);
  const [metaBusy, setMetaBusy] = React.useState(false);
  const [metaError, setMetaError] = React.useState<string | null>(null);

  const loadMeta = React.useCallback(async () => {
    try {
      const res = await fetch("/api/auth/meta/config", {
        credentials: "include",
      });
      if (res.ok) setMetaConfig((await res.json()) as MetaConfig);
    } catch {
      // Non-fatal: the card just stays in its disconnected state.
    }
  }, []);

  React.useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  // Coming back from Meta's redirect. Strip the marker so a refresh
  // doesn't re-announce a connection that happened minutes ago.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("meta");
    const failed = params.get("error");
    if (!result && !failed) return;
    if (failed) {
      // Meta's own words — the callback couldn't show them, so it passed
      // them back through the URL.
      setMetaError(failed);
    } else if (result === "partial") {
      setMetaError(
        "Connected, but some permissions were not granted. Reconnect and accept all of them.",
      );
    }
    params.delete("meta");
    params.delete("error");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : ""),
    );
  }, []);

  // Full-page redirect, not the JS SDK popup.
  //
  // FB.login with response_type=code sends the dialog an internal
  // redirect_uri we can't reproduce server-side, so Meta rejects the
  // exchange with "make sure your redirect_uri is identical". The
  // redirect flow owns both ends of that value, so they always match.
  const connectMeta = async () => {
    setMetaError(null);
    if (!metaConfig?.configured) {
      setMetaError(
        "Meta isn't configured on the server yet (app id / login config id).",
      );
      return;
    }
    setMetaBusy(true);
    try {
      const res = await fetch(
        `/api/auth/meta/start?returnTo=${encodeURIComponent("/marketing")}`,
        { credentials: "include" },
      );
      const body = (await res.json()) as { url?: string; message?: string };
      if (!res.ok || !body.url) {
        throw new Error(body.message ?? "Could not start the Meta connection.");
      }
      window.location.href = body.url;
    } catch (err) {
      setMetaError((err as Error).message);
      setMetaBusy(false);
    }
  };

  const disconnectMeta = async () => {
    setMetaBusy(true);
    try {
      await fetch("/api/auth/meta", {
        method: "DELETE",
        credentials: "include",
      });
      setMetaError(null);
      await loadMeta();
    } finally {
      setMetaBusy(false);
    }
  };

  // Feeds come from their own endpoint — /api/feeds is real, where the
  // campaigns one still 404s. Without this the page had no way to show a
  // feed at all: creating one succeeded and then vanished from the UI.
  const loadFeeds = React.useCallback(async () => {
    try {
      const res = await client.feeds.list();
      setFeeds(res.items);
    } catch {
      // A feed-list failure must not blank the whole page; the campaign
      // fetch owns the error banner.
      setFeeds([]);
    }
  }, []);

  React.useEffect(() => {
    void loadFeeds();
  }, [loadFeeds]);

  // Pause / resume. Refetched rather than patched in place because the
  // list carries derived fields (listingCount, lastSyncedAt) the update
  // response doesn't return — splicing the response into the row would
  // blank both.
  const toggleFeedStatus = React.useCallback(
    async (feed: Feed) => {
      const next = feed.status === "disabled" ? "live" : "disabled";
      await client.feeds.update(feed._id, { status: next });
      await loadFeeds();
    },
    [loadFeeds],
  );

  // Publishing opens the shared modal, which resolves the business
  // portfolios live and owns the call.
  const [publishFeed, setPublishFeed] = React.useState<Feed | null>(null);
  // Removing the catalog is destructive on Meta's side, so it asks first.
  const [unpublishFeed, setUnpublishFeed] = React.useState<Feed | null>(null);
  const [unpublishBusy, setUnpublishBusy] = React.useState(false);
  const [unpublishError, setUnpublishError] = React.useState<string | null>(
    null,
  );

  const confirmUnpublish = async () => {
    if (!unpublishFeed) return;
    setUnpublishBusy(true);
    setUnpublishError(null);
    try {
      await client.feeds.unpublishFromMeta(unpublishFeed._id);
      setUnpublishFeed(null);
      await loadFeeds();
    } catch (err) {
      setUnpublishError((err as Error).message);
    } finally {
      setUnpublishBusy(false);
    }
  };

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(pageSize),
    });
    void fetch(`/api/marketing/campaigns?${qs}`, { credentials: "include" })
      .then(async (res) => {
        // No campaigns endpoint on the API yet — treat "not found" as an
        // empty list so the page reads as "nothing here" rather than
        // throwing an error at the user.
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return (await res.json()) as CampaignListResponse;
      })
      .then((res) => {
        if (!alive) return;
        setData(res ?? EMPTY);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [page, pageSize]);

  const meta = data?.meta;
  const items = data?.items ?? null;

  const feedItems = feeds ?? [];
  const counts = {
    campaign: (items ?? []).filter((c) => c.type === "campaign").length,
    post: (items ?? []).filter((c) => c.type === "post").length,
    feed: feedItems.length,
  };

  const visibleCampaigns =
    tab === "feed" ? [] : (items ?? []).filter((c) => c.type === tab);
  const visibleFeeds =
    tab === "feed"
      ? feedItems.filter(
          (f) => feedStatus === "all" || f.status === feedStatus,
        )
      : [];
  const loaded = items !== null && feeds !== null;
  const isEmpty = visibleCampaigns.length === 0 && visibleFeeds.length === 0;
  const isFeeds = tab === "feed";
  const metaConnected = Boolean(metaConfig?.connection);

  const startCreate = (kind: CreateKind) => {
    setCreateOpen(false);
    navigate(
      kind === "campaign" ? "/marketing/campaign/new" : "/marketing/post/new",
    );
  };

  return (
    <AppShell>
      <CreatePostOrCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onContinue={startCreate}
      />
      <PublishToMetaModal
        open={publishFeed !== null}
        feed={publishFeed}
        onClose={() => setPublishFeed(null)}
        onPublished={() => void loadFeeds()}
      />
      <ConfirmModal
        open={unpublishFeed !== null}
        title="Remove from Commerce Manager?"
        confirmLabel="Remove catalog"
        busy={unpublishBusy}
        error={unpublishError}
        onConfirm={() => void confirmUnpublish()}
        onClose={() => {
          setUnpublishFeed(null);
          setUnpublishError(null);
        }}
        body={
          <>
            This deletes the catalog{" "}
            <span className="text-foreground">
              {unpublishFeed?.meta?.catalogName ?? "this feed created"}
            </span>{" "}
            from your Meta business, along with its products. Anything using
            it — ads, shops, a WhatsApp catalog — stops showing those
            listings.
            <br />
            <br />
            The feed itself and its CSV URL stay here, so you can publish it
            again later.
          </>
        }
      />
      <div className="flex flex-1 flex-col space-y-6">
        {/* Title, actions and tabs are one block: the tabs label the
            table directly below them, so they belong with the header
            rather than floating midway between it and the card.
            `items-start` is load-bearing — it keeps the H1 at the same
            distance from the top nav as every other list page, while the
            taller action cards run down to meet the tabs. */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0">
              <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
                Marketing
              </h1>
              {/* No count line here — the tabs below carry the counts, and
                the same number in two places drifts. */}
            </div>

            <div className="flex shrink-0 flex-wrap gap-3">
              <SettingsCard
                title={metaConnected ? "Meta connected" : "Connect Meta"}
                subtitle={
                  metaBusy
                    ? "Talking to Meta…"
                    : (metaError ??
                      (metaConnected
                        ? (metaConfig?.connection?.businesses?.[0]?.name ??
                          "Business connected")
                        : "Link your Facebook and Instagram account"))
                }
                actionLabel={metaConnected ? "Disconnect" : "Connect"}
                icon={MetaLogo}
                onAction={() =>
                  void (metaConnected ? disconnectMeta() : connectMeta())
                }
              />
              <SettingsCard
                title="Create post or campaign"
                subtitle="Promote a listing across your connected channels"
                actionLabel="Create"
                icon={Plus}
                onAction={() => setCreateOpen(true)}
              />
              <SettingsCard
                title="Create feed"
                subtitle="A product feed that keeps your listings in sync with Meta"
                actionLabel="Create"
                icon={Rss}
                onAction={() => navigate("/marketing/feed/new")}
              />
            </div>
          </div>

          <Tabs
            value={tab}
            onChange={(v) => {
              setTab(v);
              setPage(1);
            }}
            options={[
              { value: "campaign", label: "Campaigns", count: counts.campaign },
              { value: "post", label: "Posts", count: counts.post },
              { value: "feed", label: "Feeds", count: counts.feed },
            ]}
          />
        </div>

        <TableCard
          // Feeds carry five columns to the campaign table's nine, so
          // they don't need the same scroll floor.
          minWidth={isFeeds ? 900 : 1240}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              // Feeds aren't paginated — an enterprise is capped at a
              // handful (limitFeedsAmount), so they all render on every
              // page. Only the campaign half is server-paged.
              total={(meta?.total ?? 0) + visibleFeeds.length}
              totalPages={meta?.pages ?? 1}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          <TableHeaderRow>
            {isFeeds ? (
              <>
                <HeaderFilter
                  className="min-w-[300px] flex-[3_1_300px]"
                  value={feedStatus}
                  options={FEED_STATUS_OPTIONS}
                  onChange={setFeedStatus}
                  menuClassName="min-w-[11rem]"
                />
                <HeaderLabel className="min-w-[120px] flex-[1_1_120px]">
                  Channel
                </HeaderLabel>
                <HeaderLabel className="min-w-[200px] flex-[2_1_200px]">
                  Catalog
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  Status
                </HeaderLabel>
                <HeaderLabel className="min-w-[100px] flex-[1_1_100px]">
                  Listings
                </HeaderLabel>
                <HeaderLabel className="min-w-[140px] flex-[1_1_140px]">
                  Last sync
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  Created
                </HeaderLabel>
              </>
            ) : (
              <>
                <HeaderLabel className="min-w-[320px] flex-[3_1_320px]">
                  Name
                </HeaderLabel>
                <HeaderLabel className="min-w-[200px] flex-[2_1_200px]">
                  Listing
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  Status
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  Payment
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  Start
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  End
                </HeaderLabel>
                <HeaderLabel className="min-w-[110px] flex-[1_1_110px]">
                  Impressions
                </HeaderLabel>
                <HeaderLabel className="min-w-[90px] flex-[1_1_90px]">
                  Clicks
                </HeaderLabel>
                <HeaderLabel className="min-w-[80px] flex-[1_1_80px]">
                  Leads
                </HeaderLabel>
              </>
            )}
          </TableHeaderRow>

          {error ? (
            <TableMessage tone="destructive">{error}</TableMessage>
          ) : !loaded ? (
            <TableLoading />
          ) : isEmpty ? (
            <EmptyState
              icon={EMPTY_STATE[tab].icon}
              title={EMPTY_STATE[tab].title}
              description={EMPTY_STATE[tab].description}
            />
          ) : (
            <>
              {visibleFeeds.map((f) => (
                <FeedRow
                  key={f._id}
                  feed={f}
                  onOpen={() => navigate(`/marketing/feed/${f._id}`)}
                  onToggleStatus={() => void toggleFeedStatus(f)}
                  onPublishMeta={
                    metaConnected && !f.meta
                      ? () => setPublishFeed(f)
                      : undefined
                  }
                  onUnpublishMeta={
                    f.meta ? () => setUnpublishFeed(f) : undefined
                  }
                />
              ))}
              {visibleCampaigns.map((c) => (
                <CampaignRow key={c._id} campaign={c} />
              ))}
            </>
          )}
        </TableCard>
      </div>
    </AppShell>
  );
}

// One empty state per tab — a generic "nothing here" can't tell you what
// the tab you're looking at is even for.
const EMPTY_STATE: Record<
  Tab,
  { icon: LucideIcon; title: string; description: string }
> = {
  campaign: {
    icon: Megaphone,
    title: "No campaigns yet",
    description:
      "Campaigns promote a listing across paid channels. Once one is running it shows up here with its delivery stats.",
  },
  post: {
    icon: MessageSquare,
    title: "No posts yet",
    description:
      "Posts publish a listing to your connected social channels. Anything you schedule or publish appears here.",
  },
  feed: {
    icon: Rss,
    title: "No feeds yet",
    description:
      "A feed keeps your listings in sync with a Meta or WhatsApp catalog, refreshed automatically every time Meta fetches it.",
  },
};

const EMPTY: CampaignListResponse = {
  items: [],
  meta: {
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
    counts: { all: 0, running: 0, pending: 0, finished: 0 },
  },
};

function CampaignRow({ campaign }: { campaign: Campaign }) {
  return (
    <div className="flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-4 transition-colors last:border-b-0 hover:bg-muted/60">
      <div className="flex min-w-[320px] flex-[3_1_320px] items-center gap-3">
        <RowActionsMenu />
        <TypeIcon type={campaign.type} />
        <div className="min-w-0">
          <div className="truncate font-sans text-[14px] font-medium text-foreground">
            {campaign.name}
          </div>
          <div className="truncate font-mono text-[11px] capitalize text-muted-foreground">
            {campaign.type}
          </div>
        </div>
      </div>
      <div className="min-w-[200px] flex-[2_1_200px] min-w-0">
        <div className="truncate font-sans text-[13px] text-foreground">
          {campaign.listingName ?? "—"}
        </div>
        {campaign.listingRef && (
          <div className="truncate font-mono text-[10px] text-muted-foreground">
            {campaign.listingRef}
          </div>
        )}
      </div>
      <div className="min-w-[110px] flex-[1_1_110px]">
        <StatusBadge
          label={cap(campaign.status)}
          tone={campaign.status === "running" ? "success" : "muted"}
        />
      </div>
      <div className="min-w-[110px] flex-[1_1_110px]">
        <span
          className={cn(
            "font-mono text-[11px] uppercase tracking-[0.06em]",
            campaign.payment === "failed"
              ? "text-destructive"
              : campaign.payment === "paid"
                ? "text-foreground/80"
                : "text-muted-foreground",
          )}
        >
          {cap(campaign.payment)}
        </span>
      </div>
      <Num
        value={formatDate(campaign.start)}
        className="min-w-[110px] flex-[1_1_110px]"
      />
      <Num
        value={formatDate(campaign.end)}
        className="min-w-[110px] flex-[1_1_110px]"
      />
      <Num
        value={campaign.impressions.toLocaleString()}
        dim={campaign.impressions === 0}
        className="min-w-[110px] flex-[1_1_110px]"
      />
      <Num
        value={campaign.clicks.toLocaleString()}
        dim={campaign.clicks === 0}
        className="min-w-[90px] flex-[1_1_90px]"
      />
      <Num
        value={String(campaign.leads)}
        dim={campaign.leads === 0}
        className="min-w-[80px] flex-[1_1_80px]"
      />
    </div>
  );
}

/**
 * A feed's row.
 *
 * Shares the campaign columns rather than getting its own table: they are
 * three kinds of the same thing (something you publish), and splitting
 * them into separate lists is what let a created feed disappear.
 *
 * The campaign-only columns show "—" rather than a plausible-looking
 * zero. A feed has no spend, no flight dates and no delivery stats, and
 * "0 impressions" reads as "running but nobody saw it".
 */
function FeedRow({
  feed,
  onOpen,
  onToggleStatus,
  onPublishMeta,
  onUnpublishMeta,
}: {
  feed: Feed;
  onOpen: () => void;
  onToggleStatus: () => void;
  /** Only when published — there's nothing to remove otherwise. */
  onUnpublishMeta?: () => void;
  /** Absent when there's no Meta connection, or the feed is already
   *  published — republishing would mint a second catalog. */
  onPublishMeta?: () => void;
}) {
  const disabled = feed.status === "disabled";
  return (
    <div
      className={cn(
        "flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-4 transition-colors last:border-b-0 hover:bg-muted/60",
        // A paused feed is serving nothing; muting the whole row says so
        // at a glance rather than making the reader find the badge.
        disabled && "opacity-60",
      )}
    >
      <div className="flex min-w-[300px] flex-[3_1_300px] items-center gap-3">
        <RowActionsMenu
          onEdit={onOpen}
          copyUrl={resolveFeedUrl(feed.feedPath)}
          onToggleStatus={onToggleStatus}
          toggleLabel={disabled ? "Enable feed" : "Disable feed"}
          onPublishMeta={onPublishMeta}
          onUnpublishMeta={onUnpublishMeta}
          catalogUrl={
            feed.meta?.catalogId
              ? commerceManagerUrl(feed.meta.catalogId, feed.meta.businessId)
              : undefined
          }
        />
        <TypeIcon type="feed" />
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 text-left focus:outline-none focus-visible:underline"
        >
          <div className="truncate font-sans text-[14px] font-medium text-foreground hover:underline">
            {feed.name || "Untitled feed"}
          </div>
          <div className="truncate font-mono text-[11px] text-muted-foreground">
            feed
          </div>
        </button>
      </div>
      <div className="min-w-[120px] min-w-0 flex-[1_1_120px]">
        <div className="truncate font-sans text-[13px] text-foreground">
          {CHANNEL_LABEL[feed.format] ?? feed.format}
        </div>
        {feed.meta && (
          // Published: name the catalog it feeds, so the row says where
          // the listings actually go rather than just which format.
          <div
            className="truncate font-mono text-[10px] text-muted-foreground"
            title={feed.meta.catalogName ?? undefined}
          >
            {feed.meta.catalogName ?? "catalog connected"}
          </div>
        )}
      </div>
      <div className="min-w-[200px] min-w-0 flex-[2_1_200px]">
        <div className="truncate font-sans text-[13px] text-foreground">
          {FORMAT_LABEL[feed.format] ?? feed.format}
        </div>
        <div className="truncate font-mono text-[10px] text-muted-foreground">
          {feed.feedPath}
        </div>
      </div>
      <div className="min-w-[110px] flex-[1_1_110px]">
        <StatusBadge
          label={disabled ? "Disabled" : "Live"}
          tone={disabled ? "muted" : "success"}
        />
      </div>
      {/* A feed matching nothing is the failure that looks like success:
          the URL still resolves, Meta still fetches it, and the catalog
          silently stays empty. Dimming zero makes it findable. */}
      <Num
        value={
          feed.listingCount === undefined
            ? "—"
            : feed.listingCount.toLocaleString()
        }
        dim={feed.listingCount === 0}
        className="min-w-[100px] flex-[1_1_100px]"
      />
      {/* Freshness of the listings behind the feed, not of the feed doc:
          this moves when the CRM sync changes something this feed
          carries. "Never" means it has never matched a synced listing. */}
      <Num
        value={feed.lastSyncedAt ? formatDateTime(feed.lastSyncedAt) : "Never"}
        dim={!feed.lastSyncedAt}
        className="min-w-[140px] flex-[1_1_140px]"
      />
      <Num
        value={formatDate(feed.createdAt)}
        className="min-w-[110px] flex-[1_1_110px]"
      />
    </div>
  );
}

// One glyph per kind, in the same rounded chip the dashboard uses for
// button rows. Monochrome — the type is a category, not a state, so the
// only colour in the row stays on the status badge.
const TYPE_ICON: Record<
  Campaign["type"],
  ComponentType<{ className?: string }>
> = {
  post: MessageSquare,
  campaign: Megaphone,
  feed: Rss,
};

function TypeIcon({ type }: { type: Campaign["type"] }) {
  const Icon = TYPE_ICON[type];
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground/70">
      <Icon className="h-4 w-4" />
    </div>
  );
}

// Row actions, matching the chevron menus on the other lists. Campaigns
// and posts stay no-ops until the marketing endpoints exist; feeds pass
// a real `onEdit` and the URL to hand to Meta.
function RowActionsMenu({
  onEdit,
  copyUrl,
  onToggleStatus,
  toggleLabel,
  onPublishMeta,
  onUnpublishMeta,
  catalogUrl,
}: {
  onEdit?: () => void;
  copyUrl?: string;
  onToggleStatus?: () => void;
  toggleLabel?: string;
  onPublishMeta?: () => void;
  onUnpublishMeta?: () => void;
  /** Commerce Manager deep link. Only set once a catalog exists. */
  catalogUrl?: string;
}) {
  // 'idle' → 'copied' | 'failed', reverting after a beat. The menu is
  // held open through the transition (onSelect is prevented from closing
  // it) because a label that changes as the menu disappears tells the
  // user nothing about whether the copy actually worked.
  const [copyState, setCopyState] = React.useState<
    "idle" | "copied" | "failed"
  >("idle");
  React.useEffect(() => {
    if (copyState === "idle") return;
    const t = setTimeout(() => setCopyState("idle"), 1600);
    return () => clearTimeout(t);
  }, [copyState]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:bg-muted focus-visible:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
          aria-label="Marketing item actions"
        >
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[12rem]">
        <DropdownMenuItem onSelect={() => onEdit?.()}>
          <span className="flex-1">Edit</span>
          <Edit3 />
        </DropdownMenuItem>
        {copyUrl && (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault(); // keep the menu open to show the result
              void copyText(copyUrl).then((ok) =>
                setCopyState(ok ? "copied" : "failed"),
              );
            }}
          >
            <span
              className={cn(
                "flex-1",
                copyState === "failed" && "text-destructive",
              )}
            >
              {copyState === "copied"
                ? "Copied"
                : copyState === "failed"
                  ? "Copy failed"
                  : "Copy feed URL"}
            </span>
            {copyState === "copied" ? <Check /> : <Link2 />}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => undefined}>
          <span className="flex-1">Duplicate</span>
          <Copy />
        </DropdownMenuItem>
        {catalogUrl && (
          <DropdownMenuItem
            onSelect={() =>
              // noopener/noreferrer: the opened tab gets no handle back
              // to this one, and Meta learns nothing about where the
              // click came from.
              window.open(catalogUrl, "_blank", "noopener,noreferrer")
            }
          >
            <span className="flex-1">Open in Commerce Manager</span>
            <ExternalLink />
          </DropdownMenuItem>
        )}
        {onPublishMeta && (
          <DropdownMenuItem onSelect={() => void onPublishMeta()}>
            <span className="flex-1">Publish to Meta</span>
            <Rss />
          </DropdownMenuItem>
        )}
        {onUnpublishMeta && (
          <DropdownMenuItem onSelect={() => void onUnpublishMeta()}>
            <span className="flex-1">Remove from Commerce Manager</span>
            <Trash2 />
          </DropdownMenuItem>
        )}
        {onToggleStatus && (
          <DropdownMenuItem onSelect={() => void onToggleStatus()}>
            <span className="flex-1">{toggleLabel}</span>
            {toggleLabel?.startsWith("Enable") ? <Play /> : <Pause />}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Num({
  value,
  dim,
  className,
}: {
  value: string;
  dim?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "truncate font-mono text-[12px] font-medium tabular-nums",
        dim ? "text-muted-foreground" : "text-foreground/80",
        className,
      )}
    >
      {value}
    </div>
  );
}

function cap(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// Date + time: "last sync" is often same-day, and a bare date can't tell
// "an hour ago" from "this morning before the CRM run".
function formatDateTime(d: string): string {
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(d: string | null): string {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
